import { ReactElement, useMemo, useRef } from 'react';
import { TFunction } from 'i18next';
import { useUITranslation } from '../../hooks';
import styles from './ui-filter.module.scss';
import { UIPopover, UIPopoverPlacement, UIPropoverRef } from '../ui-popover';
import { UIMenu, UIMenuItem } from '../ui-menu';
import { UIFilterHeader } from './ui-filter-header';
import { DateFilterEnum } from '.';
import { blue, charcoal } from '../../styles';
import { UIIcon } from '../ui-icon';

type UIDatePresetFilterItemProps = {
    timeType: DateFilterEnum;
    popoverPlacement?: UIPopoverPlacement;
    onDateChange: (type: DateFilterEnum) => void;
};

export function UIDatePresetFilterItem(
    props: UIDatePresetFilterItemProps
): ReactElement {
    const { t } = useUITranslation();
    const { timeType, popoverPlacement, onDateChange } = props;
    const ref = useRef<UIPropoverRef>(null);

    function onClickOutside() {
        ref.current?.close();
    }

    function onItemPress(type: DateFilterEnum) {
        ref.current?.close();
        onDateChange(type);
    }

    const text = useMemo(() => {
        return mapTimeType(timeType, t);
    }, [timeType, t]);

    const items: { type: DateFilterEnum; onClick: () => void }[] = [
        {
            type: DateFilterEnum.Today,
            onClick: () => onItemPress(DateFilterEnum.Today)
        },
        {
            type: DateFilterEnum.Last7Days,
            onClick: () => onItemPress(DateFilterEnum.Last7Days)
        },
        {
            type: DateFilterEnum.Last4Weeks,
            onClick: () => onItemPress(DateFilterEnum.Last4Weeks)
        },
        {
            type: DateFilterEnum.Last3Months,
            onClick: () => onItemPress(DateFilterEnum.Last3Months)
        },
        {
            type: DateFilterEnum.Last6Months,
            onClick: () => onItemPress(DateFilterEnum.Last6Months)
        },
        {
            type: DateFilterEnum.Last12Months,
            onClick: () => onItemPress(DateFilterEnum.Last12Months)
        },
        {
            type: DateFilterEnum.MonthToDate,
            onClick: () => onItemPress(DateFilterEnum.MonthToDate)
        },
        {
            type: DateFilterEnum.YearToDate,
            onClick: () => onItemPress(DateFilterEnum.YearToDate)
        },
        {
            type: DateFilterEnum.Custom,
            onClick: () => {
                onItemPress(DateFilterEnum.Custom);
            }
        },
        {
            type: DateFilterEnum.AllTime,
            onClick: () => onItemPress(DateFilterEnum.AllTime)
        }
    ];

    return (
        <UIPopover
            ref={ref}
            popoverPadding={8}
            width={175}
            placement={popoverPlacement}
            triggerNode={
                <button className={styles.buttonContainer}>
                    <UIFilterHeader
                        text={text}
                        icon={
                            <UIIcon
                                name="calendar"
                                size={18}
                                color={charcoal}
                            />
                        }
                    />
                </button>
            }
            onClickOutside={onClickOutside}
        >
            <UIMenu scroll>
                {items.map((item, index) => (
                    <UIMenuItem
                        key={index}
                        text={mapTimeType(item.type, t)}
                        onClick={item.onClick}
                        leftIconName={
                            item.type === timeType ? 'check' : undefined
                        }
                        leftIconColor={blue}
                    />
                ))}
            </UIMenu>
        </UIPopover>
    );
}

function mapTimeType(timeType: DateFilterEnum, t: TFunction): string {
    switch (timeType) {
        case DateFilterEnum.Today:
            return t('components.ui_date_preset_filter_item.today');
        case DateFilterEnum.Last7Days:
            return t('components.ui_date_preset_filter_item.last_7_days');
        case DateFilterEnum.Last4Weeks:
            return t('components.ui_date_preset_filter_item.last_4_weeks');
        case DateFilterEnum.Last3Months:
            return t('components.ui_date_preset_filter_item.last_3_months');
        case DateFilterEnum.Last6Months:
            return t('components.ui_date_preset_filter_item.last_6_months');
        case DateFilterEnum.Last12Months:
            return t('components.ui_date_preset_filter_item.last_12_months');
        case DateFilterEnum.MonthToDate:
            return t('components.ui_date_preset_filter_item.month_to_date');
        case DateFilterEnum.YearToDate:
            return t('components.ui_date_preset_filter_item.year_to_date');
        case DateFilterEnum.Custom:
            return t('components.ui_date_preset_filter_item.custom');
        case DateFilterEnum.AllTime:
            return t('components.ui_date_preset_filter_item.all');
    }
}
